/**
 * Rebuilding the table from OCR bounding boxes.
 *
 * A nutrition panel is a table, and the single most damaging thing a scanner
 * can do is forget that. Read as a stream of words, this:
 *
 *     Sodium        180 mg        8%
 *
 * becomes "Sodium 180 mg 8", and a regex looking for digits near "sodium" can
 * just as easily return 8 as 180. Worse, `1808 mg` is one concatenation away.
 *
 * The geometry is what removes the ambiguity. Words on the same baseline belong
 * to one nutrient; the horizontal position then separates the name from the
 * amount from the percent column. Nothing here guesses — if a row has no
 * numeric cell, it reports none.
 */

import type { OCRLine, OCRResult, OCRWord } from './types';
import { cleanToken, isPercent, matchNutrient, readNumber, readUnit, type Unit } from './normalize';

export interface TableCell {
  text: string;
  x: number;
  width: number;
}

export interface TableRow {
  /** Everything left of the first numeric cell. */
  label: string;
  /** Non-numeric words right of it, for labels that print the value first. */
  trailing: string;
  amount: number | null;
  unit: Unit | null;
  /** Percent daily value, kept strictly separate from `amount`. */
  dailyValue: number | null;
  /** 0..1, from the OCR confidence of the words that formed this row. */
  confidence: number;
  /** True when a character substitution was needed to read this row. */
  corrected: boolean;
  y: number;
  raw: string;
}

/**
 * Group words into visual lines.
 *
 * OCR engines already emit lines, but they merge across columns on multi-column
 * labels and split when a row is slightly rotated. Regrouping from word boxes
 * by vertical overlap is more robust, and it is what lets a tilted photo still
 * produce correct rows.
 */
export function groupIntoLines(words: OCRWord[]): OCRLine[] {
  const usable = words.filter((w) => cleanToken(w.text).length > 0);
  if (usable.length === 0) return [];

  const sorted = [...usable].sort((a, b) => a.y - b.y);
  const medianHeight = median(sorted.map((w) => w.height)) || 12;
  // Two words are on the same line when their centres are within roughly half
  // a character height. Tighter than that splits rows on tilted photos; looser
  // merges adjacent rows on dense labels.
  const tolerance = medianHeight * 0.6;

  const lines: OCRWord[][] = [];
  for (const word of sorted) {
    const centre = word.y + word.height / 2;
    const target = lines.find((line) => {
      const lineCentre =
        line.reduce((s, w) => s + w.y + w.height / 2, 0) / line.length;
      return Math.abs(lineCentre - centre) <= tolerance;
    });
    if (target) target.push(word);
    else lines.push([word]);
  }

  return lines
    .map((line) => {
      const ordered = [...line].sort((a, b) => a.x - b.x);
      const x = Math.min(...ordered.map((w) => w.x));
      const y = Math.min(...ordered.map((w) => w.y));
      const right = Math.max(...ordered.map((w) => w.x + w.width));
      const bottom = Math.max(...ordered.map((w) => w.y + w.height));
      return {
        text: ordered.map((w) => w.text).join(' '),
        confidence: ordered.reduce((s, w) => s + w.confidence, 0) / ordered.length,
        words: ordered,
        x,
        y,
        width: right - x,
        height: bottom - y,
      };
    })
    .sort((a, b) => a.y - b.y);
}

/**
 * Split one line into label / amount / daily value.
 *
 * Walks left to right and stops treating words as part of the name at the first
 * numeric cell — which is what stops "8%" in the third column being read back
 * into the amount, and stops a name containing a digit from swallowing the
 * value.
 */
export function parseRow(line: OCRLine): TableRow | null {
  const words = line.words;
  if (words.length === 0) return null;

  let labelEnd = words.length;
  let amount: number | null = null;
  let unit: Unit | null = null;
  let dailyValue: number | null = null;
  let corrected = false;

  for (let i = 0; i < words.length; i++) {
    const raw = words[i]!.text;
    const token = cleanToken(raw);
    if (token.length === 0) continue;

    /*
     * Percent first.
     *
     * A percent daily value is a number too, and if it is tested after the
     * amount it becomes the amount whenever the printed amount was missed.
     * Checking it first means the worst case is a missing amount rather than
     * a wrong one.
     */
    if (isPercent(token)) {
      const pct = readNumber(token.replace('%', ''));
      /*
       * The FIRST percent on the row, not the last.
       *
       * "Potassium 210mg 4% - Niacin 50%" is one visual row carrying two
       * nutrients, and only one of them is in our vocabulary — so it cannot be
       * split, and a last-wins rule handed potassium the niacin percentage.
       * Taking the first keeps the value that belongs to the nutrient the row
       * was attributed to.
       */
      if (pct !== null && dailyValue === null) {
        dailyValue = pct.value;
        if (labelEnd === words.length) labelEnd = i;
        corrected = corrected || pct.corrected;
      }
      continue;
    }

    /*
     * Fused first: "35mg" is one token, not a number.
     *
     * This used to run only AFTER `readNumber` succeeded, which made it
     * unreachable for the tokens it exists for. `17g` slipped through because
     * `g` happens to be in the letter-to-digit table, so `readNumber` returned
     * 179 and the split then corrected it — but `mg`, `mcg`, `ml` and `kcal`
     * contain letters with no digit twin, so `readNumber` failed and the value
     * was swallowed into the label. Every sodium, cholesterol and potassium
     * figure on a US panel was being lost this way.
     */
    if (amount === null) {
      const fused = splitFused(token);
      if (fused !== null) {
        amount = fused.value;
        unit = fused.unit;
        corrected = corrected || fused.corrected;
        if (labelEnd === words.length) labelEnd = i;
        continue;
      }
    }

    const num = readNumber(token);
    if (num !== null && amount === null) {
      // A bare number that is really part of the name ("Omega 3") is rejected
      // by requiring it to be the first numeric cell AND to sit right of the
      // name — which the left-to-right walk already guarantees.
      amount = num.value;
      corrected = corrected || num.corrected;
      if (labelEnd === words.length) labelEnd = i;

      const next = words[i + 1];
      if (next) {
        const u = readUnit(next.text, true);
        if (u !== null) {
          unit = u.unit;
          corrected = corrected || u.corrected;
          i++;
        }
      }
      continue;
    }

    if (amount !== null && unit === null) {
      const u = readUnit(token, true);
      if (u !== null) {
        unit = u.unit;
        corrected = corrected || u.corrected;
      }
    }
  }

  const label = words
    .slice(0, labelEnd)
    .map((w) => w.text)
    .join(' ')
    .trim();

  /*
   * The words AFTER the amount, kept for rows that print it in the middle.
   *
   * "Includes 0g Added Sugars" names the nutrient on the far side of its own
   * value, so a label taken from the left of the first number reads
   * "Includes" and matches nothing. The caller tries this when the left side
   * does not name a nutrient.
   */
  const trailing = words
    .slice(labelEnd)
    .map((w) => w.text)
    .filter((t) => !/^[<~≈]?[0-9]/.test(cleanToken(t)) && !isPercent(t))
    .join(' ')
    .trim();

  if (label.length === 0 && amount === null) return null;

  return {
    label,
    trailing,
    amount,
    unit,
    dailyValue,
    confidence: line.confidence,
    corrected,
    y: line.y,
    raw: line.text,
  };
}

/** "180mg" arrives as one token more often than not. */
export function splitFused(token: string): { value: number; unit: Unit; corrected: boolean } | null {
  const m = /^([<~≈]?[0-9OolIiZzSsGgBqe.,]+)\s*([a-zA-Zµ0-9]{1,4})$/.exec(cleanToken(token));
  if (m === null) return null;
  const num = readNumber(m[1]!);
  if (num === null) return null;
  const unit = readUnit(m[2]!, true);
  if (unit === null) return null;
  return { value: num.value, unit: unit.unit, corrected: num.corrected || unit.corrected };
}

export function buildRows(ocr: OCRResult): TableRow[] {
  const lines = ocr.lines.length > 0 && ocr.lines.every((l) => l.words.length > 0)
    ? ocr.lines
    : groupIntoLines(ocr.words);

  return lines
    .flatMap((line) => splitColumns(line).map(parseRow))
    .filter((r): r is TableRow => r !== null);
}

/**
 * Split a line that carries two nutrients side by side.
 *
 * The vitamin block of a US panel prints two per row:
 *
 *     Vitamin D 1.5mcg 8%  •  Iron 0.9mg 6%
 *
 * Read as one row, the left nutrient takes the amount and the right one is
 * lost entirely — along with its percentage, which lands on the left one.
 *
 * The split is deliberately conservative. Every row has a wide gap before the
 * percent column, so a gap alone is not evidence; splitting there would tear
 * the daily value off the nutrient it belongs to. A line is only divided when
 * BOTH halves independently name a nutrient, which "Total Fat 0.5g | 1%" never
 * does.
 */
export function splitColumns(line: OCRLine): OCRLine[] {
  const words = line.words;
  if (words.length < 4) return [line];

  const gaps: Array<{ index: number; size: number }> = [];
  for (let i = 1; i < words.length; i++) {
    const previous = words[i - 1]!;
    gaps.push({ index: i, size: words[i]!.x - (previous.x + previous.width) });
  }
  if (gaps.length === 0) return [line];

  const widest = gaps.reduce((a, b) => (b.size > a.size ? b : a));
  const typical = median(gaps.map((g) => g.size).filter((n) => n > 0)) || 1;
  // A column break is much wider than the space between words. Below this it
  // is just kerning, and splitting would fragment a single nutrient.
  if (widest.size < Math.max(typical * 3, 20)) return [line];

  const left = words.slice(0, widest.index);
  const right = words.slice(widest.index);
  if (left.length === 0 || right.length === 0) return [line];

  if (!namesANutrient(left) || !namesANutrient(right)) return [line];
  return [asLine(left, line), asLine(right, line)];
}

/** Does this run of words contain a nutrient name? */
function namesANutrient(words: OCRWord[]): boolean {
  const text = words
    .map((w) => w.text)
    .filter((t) => !/[0-9]/.test(t))
    .join(' ');
  return matchNutrient(text) !== null;
}

function asLine(words: OCRWord[], from: OCRLine): OCRLine {
  const x = Math.min(...words.map((w) => w.x));
  const right = Math.max(...words.map((w) => w.x + w.width));
  return {
    text: words.map((w) => w.text).join(' '),
    confidence: from.confidence,
    words,
    x,
    y: from.y,
    width: right - x,
    height: from.height,
  };
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)]!;
}
